import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaEnvelope } from 'react-icons/fa'
import { subscribeNewsletter } from '../utils/api'

const NewsletterForm = () => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState({ type: '', message: '' })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setStatus({ type: '', message: '' })

    try {
      const res = await subscribeNewsletter(email)
      setStatus({ type: 'success', message: res.data?.message || 'Subscribed successfully!' })
      setEmail('')
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: error.response?.data?.message || 'Something went wrong. Please try again.' 
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-3">
      <form onSubmit={handleSubmit} className="flex">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-l-xl text-gray-800 placeholder-gray-500 text-sm focus:outline-none focus:border-accent-maroon transition-all"
        />
        <button 
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 bg-accent-maroon text-white font-bold rounded-r-xl hover:bg-accent-maroonDark transition-all disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? '...' : 'Sign Up'}
        </button>
      </form>
      
      {status.message && (
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-xs font-bold ${status.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
        >
          {status.message}
        </motion.p>
      )}
    </div>
  )
}

const Footer = () => {
  const socialLinks = [
    { icon: FaFacebook, href: '#' },
    { icon: FaTwitter, href: '#' },
    { icon: FaInstagram, href: '#' },
    { icon: FaLinkedin, href: '#' },
  ]

  const quickLinks = ['Home', 'Heritage', 'Missions', 'Gallery']
  const communityLinks = ['Volunteers', 'Impact', 'Leaderboard', 'Events']

  return (
    <footer id="contact" className="relative bg-gray-50 pt-32 pb-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-maroon/10 blur-[120px] rounded-full -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-maroon/5 blur-[120px] rounded-full translate-y-1/2" />

      <div className="max-w-7xl mx-auto relative">
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 text-center md:text-left"
        >
          <h2 className="text-6xl md:text-8xl lg:text-9xl font-black font-grotesk tracking-tighter leading-none mb-8">
            <span className="block text-gray-800">SAVE THE</span>
            <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">
              LAKES.</span>
          </h2>
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
            <p className="text-gray-600 text-xl md:text-2xl max-w-xl font-medium leading-relaxed">
              Join the digital movement to protect Udaipur's blue soul and golden heritage.
            </p>
            <motion.div 
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-accent-maroon text-4xl hidden md:block"
            >
              →
            </motion.div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-24">
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center border-2 border-accent-maroon/30">
                <img src="/logo.png" alt="Mission Mewar Samriddhi Logo" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-xl font-black font-grotesk text-gray-800 uppercase tracking-tighter">MEWAR SAMRIDDHI</h3>
            </div>
            <p className="text-gray-600 font-medium leading-relaxed">
              We're building the most transparent and impactful heritage preservation platform in India.
            </p>
            <div className="flex space-x-4 pt-2">
              {socialLinks.map((social, index) => {
                const Icon = social.icon
                return (
                  <motion.a
                    key={index}
                    href={social.href}
                    whileHover={{ y: -5, color: '#800000' }}
                    className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 transition-all"
                  >
                    <Icon size={18} />
                  </motion.a>
                )
              })}
            </div>
          </div>

          <div className="lg:pl-12">
            <h4 className="text-gray-800 font-black uppercase tracking-widest text-sm mb-8">Platform</h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase()}`} className="text-gray-600 hover:text-accent-maroon font-bold transition-all flex items-center group">
                    <span className="w-0 group-hover:w-4 h-0.5 bg-accent-maroon transition-all mr-0 group-hover:mr-2" />
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-gray-800 font-black uppercase tracking-widest text-sm mb-8">Community</h4>
            <ul className="space-y-4">
              {communityLinks.map((link) => (
                <li key={link}>
                  <a href="#" className="text-gray-600 hover:text-accent-maroon font-bold transition-all flex items-center group">
                    <span className="w-0 group-hover:w-4 h-0.5 bg-accent-maroon transition-all mr-0 group-hover:mr-2" />
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white/10 border border-gray-200 rounded-3xl p-8 backdrop-blur-md">
            <h4 className="text-gray-800 font-black uppercase tracking-widest text-xs mb-4">Stay Looped</h4>
            <p className="text-gray-600 text-sm mb-6 font-medium">Get exclusive updates on new missions.</p>
            <NewsletterForm />
          </div>
        </div>

        <div className="pt-12 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col items-center md:items-start">
            <p className="text-gray-700 text-sm font-bold uppercase">
              © {new Date().getFullYear()} MISSION MEWAR SAMRIDDHI. ALL RIGHTS RESERVED.
            </p>
            <p className="text-gray-600 text-[10px] tracking-widest font-black mt-1">
              CLEAN CITY. BLUE LAKES. GOLDEN HERITAGE.
            </p>
          </div>

          <motion.div whileHover={{ scale: 1.02 }} className="flex items-center space-x-3 bg-gray-100 px-6 py-3 rounded-2xl border border-gray-200 shadow-2xl">
            <span className="text-gray-600 text-[10px] font-black uppercase tracking-widest">Powered by</span>
            <div className="flex items-center space-x-1">
              <span className="text-gray-800 font-black tracking-tighter text-lg">KING</span>
              <span className="text-accent-maroon font-light tracking-[0.2em] text-xs">DIGITAL AGENCY</span>
            </div>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}

export default Footer